/**
 * The builder's document-level chords: Cmd/Ctrl+Z, its Shift twin, and Cmd/Ctrl+S.
 *
 * At the DOCUMENT rather than on the canvas, because undo is about the plan and not about whatever
 * currently holds focus: a trainer who has just tabbed to the phase band, or clicked into empty
 * space, still means "undo my last edit". A handler on a container would only fire while focus was
 * inside it, which is most of the time and therefore the worst kind of unreliable.
 *
 * ── What it deliberately does not swallow ────────────────────────────────────────────────────
 * A text field's own undo. While the trainer is typing in the session-name input, Cmd+Z means "undo
 * my typing" and the browser already does that correctly — taking it over would make the shortcut
 * jump straight past the half-typed word to the previous plan edit, and there would be no way back
 * to the text. So an event originating in an editable element is left alone entirely.
 *
 * **An `<input>` is not the same thing as a text field**, and treating the two as one is what made
 * Cmd+Z dead on the controls it is most wanted after — see `isTextEntry`.
 *
 * `event.repeat` is honoured rather than filtered: holding the shortcut down walking back through a
 * session is the behaviour every editor has, and the store's stack is bounded, so it terminates.
 *
 * ── It dispatches; it does not decide whether there is anything to undo ──────────────────────
 * This hook used to take `canUndo` / `canRedo` and return early when the stack was empty. That made
 * the "nothing to undo" case UNREACHABLE by the caller, and the caller is the only one that knows
 * why the stack is empty. It matters here: a successful save discards the history, so Cmd+Z stops
 * working the instant a trainer might reach for it after spotting a mistake — and the shortcut
 * answered with silence, which reads as a broken keybinding rather than as a rule.
 *
 * So the two flags are gone from the interface and the callbacks are always invoked while the plan
 * is editable. The store's `undo` and `redo` are already safe no-ops on an empty stack, and
 * `useSavePlanDocument` wraps `undo` to say why nothing happened. Two fewer props, and the decision
 * sits where the reason lives.
 *
 * `preventDefault` fires whenever this hook acts, empty stack included: the key IS handled here — an
 * answer of "saved changes cannot be undone" is a response — and on the builder canvas, outside a
 * text field, Cmd+Z has no browser behaviour to preserve.
 *
 * ── The shortcut is the only path, and that is a recorded gap ────────────────────────────────
 * The v3 canon puts undo and redo in "skróty klawiaturowe i menu" and draws no menu; PR-B2 builds
 * the plan menu that gives them a pointer path. Until then a pointer-only trainer cannot undo. The
 * legend bar carries the shortcut names as on-screen text, which is where they moved from an
 * `aria-describedby` on an unfocusable live region that announced them to nobody.
 *
 * ── Cmd/Ctrl+S, and why the builder owes one ────────────────────────────────────────────────
 * The 07.09 programator mock saves from the ember button in the top bar and draws no control in the
 * day panel. The panel is a focus-trapping dialog and Base UI marks the rest of the document
 * `aria-hidden` while it is up (`FloatingFocusManager`'s `ariaHidden: modal`, and `DialogPopup`
 * passes `modal: modal !== false` — so `'trap-focus'` hides the document too), so a trainer on a
 * keyboard or a screen reader had to LEAVE the day they were programming before they could save it.
 * The chord costs the mock nothing, because it draws nothing.
 *
 * Three things it does differently from the undo pair, each for its own reason:
 *
 * · **It does not defer to a text field.** Cmd+Z inside an `<input>` means "undo my typing" and the
 *   browser does that correctly; Cmd+S inside one means nothing at all, and a trainer half-way
 *   through a set cell still means "save my plan".
 * · **It always `preventDefault`s** while the plan is editable, including in every state where the
 *   save refuses. The browser's own "Save page as…" dialog over a plan editor is never what was
 *   meant. On a plan that cannot be edited the whole hook is off and the key reaches the browser
 *   untouched, exactly as the undo pair does — there is nothing to suppress it for.
 * · **`event.repeat` is DROPPED.** Holding the chord down walking back through a session is right
 *   for undo and is a queue of duplicate POSTs here.
 *
 * Like the undo pair, it dispatches without deciding: `useSavePlanDocument`'s `save` already refuses
 * a pristine tree, a save in flight, an unserialisable plan and an unanswered dialog, and words each
 * refusal into the region the trainer is listening to. Re-deriving that here would be a second
 * answer to a question one module already owns.
 */

import { useEffect } from 'react';

import { isTextEntry } from '~/shared/lib/isTextEntry';

export interface PlanShortcuts {
  /** Invoked on every Cmd/Ctrl+Z, INCLUDING when there is nothing to undo — see the header. */
  readonly undo: () => void;
  readonly redo: () => void;
  /**
   * Invoked on every Cmd/Ctrl+S, INCLUDING when the plan cannot be saved. The save itself owns
   * every refusal and announces it — see the header — including a plan built for a client, which
   * is saved by its assignment and not from here.
   */
  readonly save: () => void;
  /** Off entirely while the plan cannot be edited, so a read-only screen swallows nothing. */
  readonly enabled: boolean;
}

export function usePlanShortcuts({ undo, redo, save, enabled }: PlanShortcuts): void {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      // `metaKey` on macOS, `ctrlKey` everywhere else. Both are accepted on both platforms rather
      // than sniffing the OS: a trainer on a Mac with an external PC keyboard reaches for Ctrl, and
      // neither combination means anything else on this screen.
      if (!event.metaKey && !event.ctrlKey) return;
      const key = event.key.toLowerCase();

      if (key === 's') {
        // Always, in every state: the browser's own save dialog is never what a trainer meant on a
        // plan editor, and a refused save still owes them the reason rather than a file picker.
        event.preventDefault();
        // A held chord is a queue of duplicate saves, not a faster one.
        if (event.repeat) return;
        save();
        return;
      }

      // `event.key` is the produced character, so it is `'z'` on a QWERTY layout and `'y'` on
      // QWERTZ — which is why the redo alternative below is Shift rather than a second letter.
      if (key !== 'z') return;
      if (isTextEntry(event.target)) return;

      // Only once we know this is ours: a Cmd+Z inside a text field, or on a read-only plan, still
      // reaches the browser untouched.
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [undo, redo, save, enabled]);
}
